import { IntlMessageFormat } from 'intl-messageformat';

export enum TimeUnit {
  Seconds = 'seconds',
  Minutes = 'minutes',
  Hours = 'hours',
  Days = 'days',
  Weeks = 'weeks',
  Months = 'months',
  Years = 'years',
  Other = 'other',
}

export class TimeAgo {
  value: number;
  type: TimeUnit;

  constructor(value: number, type: TimeUnit) {
    this.value = value;
    this.type = type;
  }
}

export const calculateTimeAgo = (timeDiff: number): TimeAgo => {
  const yearsOpened: number = Math.floor(
    timeDiff / (1000 * 60 * 60 * 24 * 365),
  );
  const daysWithoutYears: number = Math.floor(
    (timeDiff % (1000 * 60 * 60 * 24 * 365)) / (1000 * 60 * 60 * 24),
  );

  if (yearsOpened > 0) {
    return new TimeAgo(yearsOpened, TimeUnit.Years);
  }

  if (daysWithoutYears >= 30) {
    const monthsOpened: number = Math.floor(daysWithoutYears / 30);

    return new TimeAgo(monthsOpened, TimeUnit.Months);
  }

  if (daysWithoutYears >= 7) {
    const weeksOpened: number = Math.floor(daysWithoutYears / 7);

    return new TimeAgo(weeksOpened, TimeUnit.Weeks);
  }

  if (daysWithoutYears > 0) {
    return new TimeAgo(daysWithoutYears, TimeUnit.Days);
  }

  const units = [TimeUnit.Hours, TimeUnit.Minutes, TimeUnit.Seconds];
  for (const unit of units) {
    let unitDivider;
    if (unit === TimeUnit.Hours) {
      unitDivider = 1000 * 60 * 60;
    } else if (unit === TimeUnit.Minutes) {
      unitDivider = 1000 * 60;
    } else {
      unitDivider = 1000;
    }

    const unitValue: number = Math.floor(timeDiff / unitDivider);
    if (unitValue > 0) {
      return new TimeAgo(unitValue, unit);
    }
  }

  return new TimeAgo(1, TimeUnit.Seconds);
};

export const renderToHuman = (timeAgo: TimeAgo): string => {
  let messageTemplate: string;

  if (TimeUnit.Seconds === timeAgo.type) {
    messageTemplate =
      '{multiplier, plural, one {секунду} few {секунди} other {секунд}}';
  } else if (TimeUnit.Minutes === timeAgo.type) {
    messageTemplate =
      '{multiplier, plural, one {хвилину} few {хвилини} other {хвилин}}';
  } else if (TimeUnit.Hours === timeAgo.type) {
    messageTemplate =
      '{multiplier, plural, one {годину} few {години} other {годин}}';
  } else if (TimeUnit.Days === timeAgo.type) {
    messageTemplate = '{multiplier, plural, one {день} few {дні} other {днів}}';
  } else if (TimeUnit.Weeks === timeAgo.type) {
    messageTemplate =
      '{multiplier, plural, one {тиждень} few {тижні} other {тижнів}}';
  } else if (TimeUnit.Months === timeAgo.type) {
    messageTemplate =
      '{multiplier, plural, one {місяць} few {місяці} other {місяців}}';
  } else if (TimeUnit.Years === timeAgo.type) {
    messageTemplate =
      '{multiplier, plural, one {рік} few {роки} other {років}}';
  } else {
    throw new Error('Unsupported time unit.');
  }

  const message = new IntlMessageFormat(messageTemplate, 'uk-UA');

  return message.format({ multiplier: timeAgo.value }) as string;
};
